import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  emptyStore,
  firstNameOf,
  initialsOf,
  maskEmail,
  maskPhone,
  roleAccessState,
  type Application,
  type EmployerAccount,
  type EmployerJob,
  type EmployerStore,
  type RoleAccessState,
} from "@/components/site/employer";
import {
  deleteEmployerJob,
  employerLogout,
  fetchEmployerWorkspace,
  getEmployerSession,
  getEmployerToken,
  markApplicationViewed,
  updateApplicationStatus,
  updateEmployerJob,
  updateEmployerPassword,
  updateEmployerProfile,
} from "@/lib/employer-api";
import { CandidateDatabase } from "@/components/site/CandidateDatabase";
import { PasswordInput } from "@/components/site/PasswordInput";
import { AccessPill, CandidateAccessBanner, UnlockCandidateCta } from "@/components/site/RoleAccess";
import { jobTags, tagGroups, toneClass } from "@/components/site/tags";

export const Route = createFileRoute("/employer")({
  head: () => {
    const t = "Employer dashboard | CLOSDD";
    const d = "Manage your sales roles, edit or remove adverts and review every pitch application in one place.";
    return {
      meta: [
        { name: "robots", content: "noindex,nofollow" },
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: EmployerDashboard,
});

type Tab = "roles" | "shortlist" | "database" | "account";
type JobTab = "advert" | "candidates";

const inputClass =
  "w-full min-h-12 rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 text-[16px] text-[#0B2160] outline-none transition placeholder:text-[#7A7F99] focus:border-[#0B2160] focus:ring-2 focus:ring-[#FF5A3C]/40";
const areaClass =
  "w-full rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 py-3 text-[16px] leading-[1.6] text-[#0B2160] outline-none transition placeholder:text-[#7A7F99] focus:border-[#0B2160] focus:ring-2 focus:ring-[#FF5A3C]/40";
const labelClass = "font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[#5E6380]";
const panelClass = "rounded-lg border border-[#E3E1D9] bg-white";
const btnClass =
  "inline-flex min-h-11 items-center justify-center rounded-[4px] bg-[#0B2160] px-[18px] text-[15px] font-extrabold text-white transition hover:bg-[#142C75]";
const ghostClass =
  "inline-flex min-h-11 items-center justify-center rounded-[4px] border-[1.5px] border-[#0B2160] bg-white px-[18px] text-[15px] font-extrabold text-[#0B2160] transition hover:bg-[#0B2160]/[0.05]";
const dangerClass =
  "inline-flex min-h-11 items-center justify-center rounded-[4px] border-[1.5px] border-[#A62B12] bg-white px-[18px] text-[15px] font-extrabold text-[#A62B12] transition hover:bg-[#FCE3DD]";

/** Mirrors the real header + sidebar + panel shell so the dashboard doesn't flash blank while the workspace fetch is in flight. */
function EmployerSkeleton() {
  const bar = "animate-pulse rounded-[4px] bg-[#E3E1D9]";
  return (
    <div className="cl min-h-screen bg-[#F4F3EE]" aria-busy="true" aria-label="Loading">
      <header className="bg-[#0B2160]">
        <div className="mx-auto flex w-full max-w-[1120px] flex-wrap items-center justify-between gap-3 px-6 py-3.5">
          <div className="flex items-center gap-3.5">
            <span className="flex items-center gap-2 text-[22px] font-extrabold tracking-[-0.03em] text-white">
              <span aria-hidden className="inline-block h-[9px] w-[9px] rounded-full bg-[#FF5A3C]" />
              closdd
            </span>
            <span className="border-l border-[#2A4393] pl-3.5 font-mono text-[11px] uppercase tracking-[0.16em] text-[#B9C6EE]">
              Employer console
            </span>
          </div>
          <div className="h-11 w-28 animate-pulse rounded-[4px] bg-white/15" />
        </div>
      </header>
      <div className="border-b border-[#E3E1D9] bg-white">
        <div className="mx-auto flex w-full max-w-[1120px] gap-2 px-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="px-3 pb-[13px] pt-4">
              <div className={`h-5 w-20 ${bar}`} />
            </div>
          ))}
        </div>
      </div>
      <main className="mx-auto grid w-full max-w-[1120px] gap-4 px-6 pb-16 pt-8">
        <div className={`h-9 w-48 ${bar}`} />
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[92px] animate-pulse rounded-lg border border-[#E3E1D9] bg-white" />
        ))}
      </main>
    </div>
  );
}

function EmployerDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("roles");
  const [store, setStore] = useState<EmployerStore>(emptyStore);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [actionError, setActionError] = useState("");
  const [loggingOut, setLoggingOut] = useState(false);
  const [viewJob, setViewJob] = useState<string | null>(null);
  const [editJob, setEditJob] = useState<string | null>(null);
  const [jobTab, setJobTab] = useState<JobTab>("advert");

  useEffect(() => {
    let cancelled = false;
    if (!getEmployerToken()) {
      navigate({ to: "/login" });
      return;
    }
    // Fire the workspace fetch alongside the session check rather than after
    // it, so the skeleton waits on one round trip instead of two.
    const workspace = fetchEmployerWorkspace();
    workspace.catch(() => undefined);
    (async () => {
      const user = await getEmployerSession();
      if (!user) {
        if (!cancelled) navigate({ to: "/login" });
        return;
      }
      try {
        const s = await workspace;
        if (!cancelled) {
          setStore(s);
          setReady(true);
          setLoadError("");
        }
      } catch (err) {
        // A failed workspace fetch does not mean the session is invalid —
        // getEmployerSession() already confirmed that above. Redirecting to
        // /login here was signing employers out of a perfectly good session
        // on any transient error; show a retry instead.
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "We couldn't load your dashboard. Try again.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, retryCount]);

  /** Runs a mutation against the API, surfacing any failure as a banner instead of silently doing nothing. */
  async function act(run: () => Promise<void>) {
    setActionError("");
    try {
      await run();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    }
  }

  async function handleStatus(id: string, status: Application["status"]) {
    await act(async () => {
      const updated = await updateApplicationStatus(id, status);
      setStore((s) => ({ ...s, applications: s.applications.map((a) => (a.id === id ? updated : a)) }));
    });
  }

  async function handleJobChange(next: EmployerJob, resubmit = false) {
    await act(async () => {
      const updated = await updateEmployerJob(next.id, {
        ...(resubmit ? { resubmit: true } : {}),
        ref: next.ref,
        title: next.title,
        seniority: next.seniority,
        contract: next.contract,
        company: next.company,
        location: next.location,
        setting: next.setting,
        payStructure: next.payStructure,
        pay: next.pay,
        advert: next.advert,
        status: next.status,
      });
      setStore((s) => ({ ...s, jobs: s.jobs.map((j) => (j.id === next.id ? updated : j)) }));
    });
  }

  async function handleJobRemove(id: string) {
    await act(async () => {
      await deleteEmployerJob(id);
      setStore((s) => ({
        ...s,
        jobs: s.jobs.filter((j) => j.id !== id),
        applications: s.applications.filter((a) => a.jobId !== id),
      }));
      setViewJob(null);
    });
  }

  async function handleView(jobId: string, appId: string) {
    await act(async () => {
      const access = await markApplicationViewed(jobId, appId);
      setStore((s) => ({ ...s, access: { ...s.access, [jobId]: access } }));
    });
  }

  async function handleAccountSave(a: EmployerAccount) {
    await act(async () => {
      const updated = await updateEmployerProfile({
        company: a.company,
        contactName: a.contactName,
        email: a.email,
        phone: a.phone,
      });
      setStore((s) => ({ ...s, account: updated }));
    });
  }

  if (loadError) {
    return (
      <div className="cl grid min-h-screen place-items-center bg-[#F4F3EE] px-6">
        <div className="grid max-w-[420px] gap-4 text-center">
          <p className="text-[17px] text-[#3A3A40]">{loadError}</p>
          <button
            type="button"
            onClick={() => setRetryCount((n) => n + 1)}
            className="mx-auto min-h-11 rounded-[4px] bg-[#FF5A3C] px-6 text-[15px] font-extrabold text-[#0B2160]"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!ready || !store.account) return <EmployerSkeleton />;

  const account = store.account;
  // Exclusive, invite only roles are run by the CLOSDD team, not the employer.
  const ownJobs = store.jobs.filter((j) => !j.exclusive);
  const activeJobs = ownJobs.filter((j) => j.status === "Live" && (j.decision === undefined || j.decision === "Approved"));
  // /employer/applications already only returns applications the CLOSDD team has approved.
  const visibleApps = store.applications;
  const totalApps = visibleApps.length;
  const accessOf = (jobId: string) => roleAccessState(store, jobId);

  return (
    <div className="cl min-h-screen bg-[#F4F3EE]">
      <header className="bg-[#0B2160]">
        <div className="mx-auto flex w-full max-w-[1120px] flex-wrap items-center justify-between gap-3 px-6 py-3.5">
          <div className="flex items-center gap-3.5">
            <Link to="/" className="flex items-center gap-2 text-[22px] font-extrabold tracking-[-0.03em] text-white">
              <span aria-hidden className="inline-block h-[9px] w-[9px] rounded-full bg-[#FF5A3C]" />
              closdd
            </Link>
            <span className="hidden border-l border-[#2A4393] pl-3.5 font-mono text-[11px] uppercase tracking-[0.16em] text-[#B9C6EE] sm:block">
              Employer console
            </span>
          </div>
          <div className="flex items-center gap-[18px]">
            <Link to="/post-job" className="rounded-[4px] bg-[#FF5A3C] px-4 py-[11px] text-[15px] font-extrabold text-[#0B2160]">
              Post a role
            </Link>
            <button
              type="button"
              disabled={loggingOut}
              onClick={() => {
                setLoggingOut(true);
                void employerLogout();
                navigate({ to: "/login" });
              }}
              className="text-[15px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loggingOut ? "Logging out…" : "Log out"}
            </button>
          </div>
        </div>
      </header>

      <nav aria-label="Console" className="border-b border-[#E3E1D9] bg-white">
        <div className="mx-auto flex w-full max-w-[1120px] flex-wrap items-center justify-between gap-3 px-6">
          <div className="flex gap-2">
            {([
              { id: "roles", label: "My roles" },
              { id: "shortlist", label: "Shortlist" },
              { id: "database", label: "Find candidates" },
              { id: "account", label: "Account" },
            ] as { id: Tab; label: string }[]).map((item) => (
              <button
                key={item.id}
                type="button"
                aria-current={tab === item.id ? "page" : undefined}
                onClick={() => {
                  setTab(item.id);
                  setViewJob(null);
                  setEditJob(null);
                }}
                className={`border-b-[3px] px-3 pb-[13px] pt-4 text-[15px] ${
                  tab === item.id
                    ? "border-[#FF5A3C] font-extrabold text-[#0B2160]"
                    : "border-transparent font-semibold text-[#4A4F6A] hover:text-[#0B2160]"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <span className="py-2 text-[14px] text-[#5E6380]">{account.company || account.email}</span>
        </div>
      </nav>

      {actionError ? (
        <div className="mx-auto w-full max-w-[1120px] px-6 pt-6">
          <div className="rounded-2xl border border-tag-red/25 bg-tag-red-soft px-6 py-4 text-[14px] font-semibold text-tag-red">
            {actionError}
          </div>
        </div>
      ) : null}

      <div className="mx-auto w-full max-w-[1120px] px-6 pb-16 pt-8">
        <main className="grid gap-6">
          {tab === "roles" && editJob ? (
            <EditRolePage
              job={store.jobs.find((j) => j.id === editJob)}
              onBack={() => setEditJob(null)}
              onSave={async (next, resubmit) => {
                await handleJobChange(next, resubmit);
                setEditJob(null);
              }}
              onRemove={async (id) => {
                await handleJobRemove(id);
                setEditJob(null);
              }}
            />
          ) : tab === "roles" && viewJob ? (
            <JobDetail
              job={store.jobs.find((j) => j.id === viewJob)}
              apps={visibleApps.filter((a) => a.jobId === viewJob)}
              access={accessOf(viewJob)}
              activeTab={jobTab}
              setActiveTab={setJobTab}
              onBack={() => setViewJob(null)}
              onEdit={() => {
                setEditJob(viewJob);
                setViewJob(null);
              }}
              onView={(appId) => handleView(viewJob, appId)}
              onStatus={handleStatus}
              onJobChange={handleJobChange}
              onJobRemove={handleJobRemove}
            />
          ) : tab === "roles" ? (
            <MyRoles
              jobs={ownJobs}
              liveCount={activeJobs.length}
              totalApps={totalApps}
              countFor={(id: string) => visibleApps.filter((a) => a.jobId === id).length}
              newFor={(id) => visibleApps.filter((a) => a.jobId === id && a.status === "New").length}
              accessFor={accessOf}
              onOpen={setViewJob}
              onEdit={setEditJob}
            />
          ) : tab === "shortlist" ? (
            <Shortlist
              jobs={ownJobs}
              apps={visibleApps.filter((a) => a.status === "Shortlisted")}
              accessFor={accessOf}
              onStatus={handleStatus}
            />
          ) : tab === "database" ? (
            <CandidateDatabase />
          ) : (
            <AccountPanel
              account={account}
              onSave={handleAccountSave}
              jobs={ownJobs}
              accessFor={accessOf}
              countFor={(id) => visibleApps.filter((a) => a.jobId === id).length}
            />
          )}
        </main>
      </div>
    </div>
  );
}

/** "Live", "In review", "Rejected", "Paused" or "Closed" — what the employer sees on a role (design: My roles). */
function roleStatus(job: EmployerJob): { label: string; cls: string } {
  if (job.decision === "Rejected") return { label: "Rejected", cls: "bg-[#FCE3DD] text-[#A62B12]" };
  if (job.decision === "Pending") return { label: "In review", cls: "bg-[#FFE9A8] text-[#6B4A00]" };
  if (job.status === "Live") return { label: "Live", cls: "bg-[#E2F3E9] text-[#0B6B40]" };
  return { label: job.status, cls: "bg-[#EEEDE7] text-[#4A4F6A]" };
}

const isOpenRole = (job: EmployerJob) => job.status === "Live" && (job.decision === undefined || job.decision === "Approved");
const roleMeta = (job: EmployerJob) => [job.location, job.setting, job.contract, job.pay || job.payStructure].filter(Boolean).join(" · ");
const jobLink = (job: EmployerJob) => `/jobs/${(job.ref || job.id).toLowerCase()}`;
const UNLOCK_LABEL = "£99";

function StatusChip({ job }: { job: EmployerJob }) {
  const s = roleStatus(job);
  return <span className={`rounded-full px-2.5 py-[3px] text-[12px] font-bold ${s.cls}`}>{s.label}</span>;
}

/** My roles (design: Main.dc.html in the employer console). */
function MyRoles({
  jobs,
  liveCount,
  totalApps,
  countFor,
  newFor,
  accessFor,
  onOpen,
  onEdit,
}: {
  jobs: EmployerJob[];
  liveCount: number;
  totalApps: number;
  countFor: (id: string) => number;
  newFor: (id: string) => number;
  accessFor: (id: string) => RoleAccessState;
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
}) {
  const [unlocking, setUnlocking] = useState<EmployerJob | null>(null);
  const link = "text-[#5E6380] underline underline-offset-2 hover:text-[#0B2160]";

  return (
    <section className="flex flex-col gap-4">
      <div className="mb-1">
        <h1 className="text-[32px] font-extrabold tracking-[-1px] text-[#0B2160]">My roles</h1>
        <p className="mt-1.5 text-[15px] text-[#5E6380]">
          {jobs.length} role{jobs.length === 1 ? "" : "s"} · {liveCount} live · {totalApps} application{totalApps === 1 ? "" : "s"}
        </p>
      </div>

      {jobs.length === 0 ? (
        <div className={`${panelClass} px-6 py-12`}>
          <Empty text="You haven't posted a role yet. Post one and applications land here." />
        </div>
      ) : null}

      {jobs.map((job) => {
        const total = countFor(job.id);
        const fresh = newFor(job.id);
        const access = accessFor(job.id);
        const open = isOpenRole(job);
        const inReview = job.decision === "Pending";
        const rejected = job.decision === "Rejected";
        return (
          <article key={job.id} className="overflow-hidden rounded-lg border border-[#E3E1D9] bg-white">
            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 px-[22px] py-5">
              <div className="min-w-0 flex-[1_1_320px]">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-[20px] font-extrabold tracking-[-0.3px] text-[#0B2160]">{job.title}</h2>
                  <StatusChip job={job} />
                </div>
                {job.ref ? <p className="mt-1.5 font-mono text-[12px] tracking-[1px] text-[#5E6380]">REF {job.ref}</p> : null}
                <p className="mt-1 text-[14px] text-[#5E6380]">
                  {roleMeta(job)}
                  {open ? (
                    <>
                      {" · "}
                      <a href={jobLink(job)} target="_blank" rel="noopener" className={link}>
                        View job
                      </a>
                    </>
                  ) : null}
                  {!rejected ? (
                    <>
                      {" · "}
                      <button type="button" onClick={() => onEdit(job.id)} className={link}>
                        Edit
                      </button>
                    </>
                  ) : null}
                </p>
              </div>

              {rejected ? (
                <button
                  type="button"
                  onClick={() => onEdit(job.id)}
                  className="flex h-11 items-center rounded-[4px] border-[1.5px] border-[#0B2160] px-[18px] text-[15px] font-extrabold text-[#0B2160]"
                >
                  Fix and resubmit
                </button>
              ) : !inReview ? (
                <div className="flex flex-wrap items-center gap-5">
                  <div className="text-right">
                    <div className="text-[15px] font-extrabold text-[#0B2160]">
                      {total} applicant{total === 1 ? "" : "s"}
                    </div>
                    {fresh ? <div className="text-[13px] font-bold text-[#C2381B]">{fresh} new</div> : null}
                  </div>
                  {!access.unlocked ? (
                    <button
                      type="button"
                      onClick={() => setUnlocking(job)}
                      className="flex h-11 items-center rounded-[4px] bg-[#FF5A3C] px-4 text-[15px] font-extrabold text-[#0B2160]"
                    >
                      Unlock · {UNLOCK_LABEL}
                    </button>
                  ) : null}
                  <button type="button" onClick={() => onOpen(job.id)} className="flex h-11 items-center rounded-[4px] bg-[#0B2160] px-[18px] text-[15px] font-extrabold text-white">
                    View applicants
                  </button>
                </div>
              ) : null}
            </div>
            {inReview ? (
              <div className="border-t border-[#F3E2A6] bg-[#FFF8E1] px-[22px] py-3 text-[14px] text-[#6B4A00]">
                Closdd is checking this advert. It usually goes live within 24 hours.
              </div>
            ) : null}
            {rejected ? (
              <div className="border-t border-[#F5CFC4] bg-[#FDF0EC] px-[22px] py-3 text-[14px] text-[#8A2A12]">
                <strong>Why it was rejected:</strong> {job.decisionReason || "Closdd couldn't approve this advert. Check it over, then resubmit."}
              </div>
            ) : null}
          </article>
        );
      })}

      <p className="mt-2 text-[14px] text-[#5E6380]">
        Need help?{" "}
        <a href="mailto:info@closdd.com" className="font-bold text-[#0B2160] underline underline-offset-2">
          info@closdd.com
        </a>
      </p>

      {unlocking ? <UnlockDialog job={unlocking} applicants={countFor(unlocking.id)} onClose={() => setUnlocking(null)} /> : null}
    </section>
  );
}

/** "Unlock · £99" pop-up (design: LockedRole.dc.html). Payment itself runs on /upgrade. */
function UnlockDialog({ job, applicants, onClose }: { job: EmployerJob; applicants: number; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(11,33,96,0.45)] p-5" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ul-h"
        onClick={(e) => e.stopPropagation()}
        className="w-[min(460px,100%)] overflow-hidden rounded-[10px] bg-white shadow-[0_24px_60px_rgba(11,33,96,0.3)]"
      >
        <div className="bg-[#0B2160] p-[22px]">
          <p id="ul-h" className="text-[14px] font-bold text-[#B9C6EE]">
            Unlock {job.title}
            {job.ref ? ` · REF ${job.ref}` : ""}
          </p>
          <p className="mt-1.5 text-[40px] font-extrabold leading-none tracking-[-1.5px] text-white">{UNLOCK_LABEL}</p>
          <p className="mt-1.5 text-[14px] text-[#D3DBF4]">One-off for this role. No subscription, no fee per hire.</p>
        </div>
        <div className="flex flex-col gap-3 px-[22px] py-5">
          <p className="text-[15px] text-[#2B3150]">
            See full names, emails, phone numbers and CVs for {applicants === 1 ? "your applicant" : `all ${applicants} applicants`}.
          </p>
          <p className="flex items-start gap-2 text-[14px] text-[#0B4F30]">
            <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0B6B40" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-none">
              <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
              <path d="M8.5 12l2.5 2.5 4.5-5" />
            </svg>
            Money back within 7 days if nobody is worth a call.
          </p>
          <Link to="/upgrade" search={{ job: job.id }} className="flex h-[50px] items-center justify-center rounded-[4px] bg-[#FF5A3C] text-[16px] font-extrabold text-[#0B2160]">
            Pay {UNLOCK_LABEL} and unlock
          </Link>
          <div className="flex justify-end text-[14px]">
            <button type="button" onClick={onClose} className="font-bold text-[#4A4F6A] underline underline-offset-[3px]">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Edit role / Fix and resubmit — opens the role the employer clicked. */
function EditRolePage({
  job,
  onBack,
  onSave,
  onRemove,
}: {
  job: EmployerJob | undefined;
  onBack: () => void;
  onSave: (job: EmployerJob, resubmit: boolean) => void;
  onRemove: (id: string) => void;
}) {
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; value: string }>({ open: false, value: "" });
  if (!job) return null;
  const rejected = job.decision === "Rejected";
  return (
    <div className="flex flex-col gap-4">
      <button type="button" onClick={onBack} className="self-start text-[14px] font-bold text-[#4A4F6A] hover:text-[#0B2160]">
        ← My roles
      </button>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-[32px] font-extrabold tracking-[-1px] text-[#0B2160]">{rejected ? "Fix and resubmit" : "Edit role"}</h1>
          <StatusChip job={job} />
        </div>
        <p className="mt-1.5 text-[15px] text-[#5E6380]">
          {job.ref ? <span className="font-mono text-[13px] tracking-[1px]">REF {job.ref} · </span> : null}
          {job.title}
        </p>
      </div>
      {rejected ? (
        <div className="rounded-lg border border-[#F5CFC4] bg-[#FDF0EC] px-[22px] py-3 text-[14px] text-[#8A2A12]">
          <strong>Why it was rejected:</strong> {job.decisionReason || "Closdd couldn't approve this advert. Check it over, then resubmit."}
          <span className="mt-1 block">Fix it below and save — it goes back to Closdd for review.</span>
        </div>
      ) : null}
      <div className={`${panelClass} p-[22px]`}>
        <EditJob job={job} submitLabel={rejected ? "Save and resubmit for review" : "Save changes"} onSave={(next) => onSave(next, rejected)} />
      </div>
      <section className={`${panelClass} flex flex-wrap items-center justify-between gap-4 p-[22px]`}>
        <div>
          <h2 className="text-[17px] font-extrabold text-[#0B2160]">Delete role</h2>
          <p className="mt-1 text-[14px] text-[#5E6380]">Removes the advert from Closdd. This can't be undone.</p>
        </div>
        <button
          type="button"
          onClick={() => setDeleteConfirm({ open: true, value: "" })}
          className="flex h-11 items-center rounded-[4px] border-[1.5px] border-[#A62B12] bg-white px-4 text-[15px] font-extrabold text-[#A62B12]"
        >
          Delete role
        </button>
      </section>
      <TypeToConfirmModal
        open={deleteConfirm.open}
        title={`Delete ${job.title}?`}
        message="This will permanently remove the role and every application attached to it. Type delete to confirm."
        confirmWord="delete"
        value={deleteConfirm.value}
        onChange={(value) => setDeleteConfirm({ ...deleteConfirm, value })}
        onCancel={() => setDeleteConfirm({ open: false, value: "" })}
        onConfirm={() => {
          setDeleteConfirm({ open: false, value: "" });
          onRemove(job.id);
        }}
      />
    </div>
  );
}

/** One role's applicants (design: RoleDetail.dc.html unlocked, LockedRole.dc.html locked). */
function JobDetail({
  job,
  apps,
  access,
  onBack,
  onEdit,
  onView,
  onStatus,
}: {
  job: EmployerJob | undefined;
  apps: Application[];
  access: RoleAccessState;
  activeTab?: JobTab;
  setActiveTab?: (t: JobTab) => void;
  onBack: () => void;
  onEdit: () => void;
  onView: (appId: string) => void;
  onStatus: (id: string, status: Application["status"]) => void;
  onJobChange?: (job: EmployerJob) => void;
  onJobRemove?: (id: string) => void;
}) {
  const [filter, setFilter] = useState<"All" | "Shortlisted" | "Rejected">("All");
  const [openId, setOpenId] = useState("");
  const [playing, setPlaying] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  if (!job) return null;
  const locked = !access.unlocked;
  const tabs = [
    { key: "All" as const, label: "All", count: apps.length },
    { key: "Shortlisted" as const, label: "Shortlisted", count: apps.filter((a) => a.status === "Shortlisted").length },
    { key: "Rejected" as const, label: "Not a fit", count: apps.filter((a) => a.status === "Rejected").length },
  ];
  const shown = filter === "All" ? apps : apps.filter((a) => a.status === filter);
  const outline =
    "flex h-[42px] items-center rounded-[4px] border-[1.5px] border-[#D6D3C8] bg-white px-3.5 text-[14px] font-bold text-[#0B2160]";

  return (
    <div className="flex flex-col gap-4">
      <button type="button" onClick={onBack} className="self-start text-[14px] font-bold text-[#4A4F6A] hover:text-[#0B2160]">
        ← My roles
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[32px] font-extrabold tracking-[-1px] text-[#0B2160]">{job.title}</h1>
            <StatusChip job={job} />
            {!locked ? <span className="rounded-full bg-[#0B2160] px-2.5 py-[3px] text-[12px] font-bold text-white">Unlocked</span> : null}
          </div>
          <p className="mt-1.5 text-[15px] text-[#5E6380]">
            {job.ref ? <span className="font-mono text-[13px] tracking-[1px]">REF {job.ref} · </span> : null}
            {roleMeta(job)}
          </p>
        </div>
        <div className="flex gap-2">
          {isOpenRole(job) ? (
            <a href={jobLink(job)} target="_blank" rel="noopener" className={outline}>
              View job
            </a>
          ) : null}
          <button type="button" onClick={onEdit} className={outline}>
            Edit role
          </button>
        </div>
      </div>

      {locked ? (
        <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-3.5 rounded-lg bg-[#0B2160] px-5 py-[18px]">
          <div className="min-w-0 flex-[1_1_420px]">
            <p className="text-[17px] font-extrabold text-white">Listen and shortlist for free.</p>
            <p className="mt-1 text-[15px] text-[#D3DBF4]">Unlock this role to see full names, CVs and contact details.</p>
          </div>
          <button type="button" onClick={() => setUnlocking(true)} className="flex h-11 items-center rounded-[4px] bg-[#FF5A3C] px-5 text-[15px] font-extrabold text-[#0B2160]">
            Unlock candidates · {UNLOCK_LABEL}
          </button>
        </div>
      ) : null}

      <div role="tablist" aria-label="Filter candidates" className="flex flex-wrap gap-1.5">
        {tabs.map((t) => {
          const on = filter === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setFilter(t.key)}
              className={`h-[38px] rounded-full border px-3.5 text-[14px] font-bold ${on ? "border-[#0B2160] bg-[#0B2160] text-white" : "border-[#D6D3C8] bg-white text-[#0B2160]"}`}
            >
              {t.label} · {t.count}
            </button>
          );
        })}
      </div>

      <section aria-label="Candidates" className="overflow-hidden rounded-lg border border-[#E3E1D9] bg-white">
        {shown.length === 0 ? (
          <p className="px-6 py-10 text-center text-[15px] text-[#4A4F6A]">
            {apps.length === 0 ? "No applications for this role yet." : "Nobody in this group yet."}
          </p>
        ) : null}
        {shown.map((a, i) => (
          <CandidateRow
            key={a.id}
            app={a}
            first={i === 0}
            locked={locked}
            open={openId === a.id}
            onToggle={() => {
              if (openId !== a.id) onView(a.id);
              setOpenId(openId === a.id ? "" : a.id);
            }}
            playing={playing === a.id}
            onPlay={(on) => setPlaying(on ? a.id : "")}
            onStatus={onStatus}
          />
        ))}
      </section>

      <p className="mt-6 text-[14px] text-[#5E6380]">
        Need help?{" "}
        <a href="mailto:info@closdd.com" className="font-bold text-[#0B2160] underline underline-offset-2">
          info@closdd.com
        </a>
      </p>

      {unlocking ? <UnlockDialog job={job} applicants={apps.length} onClose={() => setUnlocking(false)} /> : null}
    </div>
  );
}

function CandidateRow({
  app,
  first,
  locked,
  open,
  onToggle,
  playing,
  onPlay,
  onStatus,
}: {
  app: Application;
  first: boolean;
  locked: boolean;
  open: boolean;
  onToggle: () => void;
  playing: boolean;
  onPlay: (on: boolean) => void;
  onStatus: (id: string, status: Application["status"]) => void;
}) {
  const shortlisted = app.status === "Shortlisted";
  const notFit = app.status === "Rejected";
  const shownName = locked ? firstNameOf(app.name) : app.name;
  const facts = [
    ["Email", locked ? maskEmail(app.email) : app.email || "Not provided"],
    ["Phone", locked ? maskPhone(app.phone) : app.phone || "Not provided"],
    ["Location", app.location || "Not provided"],
    ["Experience", app.years || "Not provided"],
  ];

  return (
    <div className={first ? "" : "border-t border-[#ECEAE3]"} style={{ opacity: notFit ? 0.6 : 1 }}>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 px-5 py-3.5">
        <button type="button" aria-expanded={open} onClick={onToggle} className="flex min-w-0 flex-[1_1_220px] items-center gap-3 text-left">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#E8ECF8] text-[14px] font-extrabold text-[#0B2160]">
            {initialsOf(shownName)}
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-2 text-[16px] font-extrabold text-[#0B2160]">
              {shownName}
              {locked ? <span aria-label="Surname hidden" className="inline-block h-2.5 w-14 rounded-[3px] bg-[#E3E1D9]" /> : null}
            </span>
            <span className="mt-0.5 block text-[13px] text-[#5E6380]">
              {app.location}
              {notFit ? " · Not a fit" : ""}
            </span>
          </span>
          <svg
            aria-hidden
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#5E6380"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="ml-auto flex-none transition-transform"
            style={{ transform: open ? "rotate(180deg)" : "none" }}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        <RowPlayer url={app.hasPitch ? app.audioUrl : undefined} name={shownName} playing={playing} onPlay={onPlay} />

        <button
          type="button"
          aria-pressed={shortlisted}
          onClick={() => onStatus(app.id, shortlisted ? "New" : "Shortlisted")}
          className={`h-[42px] min-w-[128px] rounded-[4px] border-[1.5px] px-3.5 text-[14px] font-extrabold ${
            shortlisted ? "border-[#FF5A3C] bg-[#FF5A3C] text-[#0B2160]" : "border-[#0B2160] bg-[#0B2160] text-white"
          }`}
        >
          {shortlisted ? "✓ Shortlisted" : "Shortlist"}
        </button>
        <button
          type="button"
          aria-pressed={notFit}
          onClick={() => onStatus(app.id, notFit ? "New" : "Rejected")}
          className={`h-[42px] min-w-[128px] rounded-[4px] border-[1.5px] px-3.5 text-[14px] font-extrabold ${
            notFit ? "border-[#8A8EA6] bg-[#EEEDE7] text-[#4A4F6A]" : "border-[#0B2160] bg-white text-[#0B2160]"
          }`}
        >
          {notFit ? "✓ Not a fit" : "Not a fit"}
        </button>
      </div>

      {open ? (
        <div className="mx-5 mb-[18px] flex flex-wrap items-center gap-x-6 gap-y-3.5 rounded-lg border border-[#ECEAE3] bg-[#FAF9F5] px-5 py-4">
          <dl className="m-0 grid flex-[1_1_520px] gap-x-5 gap-y-2.5 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
            {facts.map(([label, value]) => (
              <div key={label}>
                <dt className="text-[12px] font-bold text-[#5E6380]">{label}</dt>
                <dd className="mt-[3px] break-all text-[14px] font-bold text-[#0B2160]">{value}</dd>
              </div>
            ))}
          </dl>
          {!locked && app.profileSlug ? (
            <a
              href={`/profile/${app.profileSlug}`}
              target="_blank"
              rel="noopener"
              aria-label={`View ${app.name}'s profile (opens in a new tab)`}
              className="inline-flex h-10 items-center self-center rounded-[4px] border-[1.5px] border-[#0B2160] bg-white px-4 text-[14px] font-extrabold text-[#0B2160]"
            >
              View profile
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Orange play button, progress bar and time, as in the design. One pitch plays at a time. */
function RowPlayer({ url, name, playing, onPlay }: { url: string | undefined; name: string; playing: boolean; onPlay: (on: boolean) => void }) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [time, setTime] = useState(0);
  const [length, setLength] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (playing) void el.play().catch(() => onPlay(false));
    else el.pause();
  }, [playing, onPlay]);

  const clock = (n: number) => `${Math.floor(n / 60)}:${String(Math.floor(n % 60)).padStart(2, "0")}`;
  return (
    <div className="flex min-w-0 flex-[1_1_240px] items-center gap-3">
      <button
        type="button"
        disabled={!url}
        aria-label={!url ? `No pitch from ${name}` : playing ? `Pause pitch from ${name}` : `Play pitch from ${name}`}
        onClick={() => onPlay(!playing)}
        className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[#FF5A3C] disabled:opacity-40"
      >
        {playing ? (
          <svg aria-hidden width="13" height="13" viewBox="0 0 24 24" fill="#0B2160">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg aria-hidden width="13" height="13" viewBox="0 0 24 24" fill="#0B2160">
            <path d="M8 5l12 7-12 7z" />
          </svg>
        )}
      </button>
      <div aria-hidden className="h-1.5 flex-1 overflow-hidden rounded-[3px] bg-[#E3E1D9]">
        <div className="h-1.5 bg-[#FF5A3C]" style={{ width: length ? `${Math.min(100, (time / length) * 100)}%` : "0%" }} />
      </div>
      <span className="whitespace-nowrap font-mono text-[12px] text-[#4A4F6A]">{url ? clock(playing || time ? time : length) : "—"}</span>
      {url ? (
        <audio
          ref={ref}
          src={url}
          preload="metadata"
          onLoadedMetadata={(e) => setLength(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)}
          onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
          onEnded={() => {
            setTime(0);
            onPlay(false);
          }}
        />
      ) : null}
    </div>
  );
}

function Shortlist({
  jobs,
  apps,
  accessFor,
  onStatus,
}: {
  jobs: EmployerJob[];
  apps: Application[];
  accessFor: (id: string) => RoleAccessState;
  onStatus: (id: string, status: Application["status"]) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();

  // One row per role: dedupe repeated roles (same title + location) so a role
  // never shows up twice, while every applicant is still listed.
  const seen = new Set<string>();
  const groups = jobs
    .filter((job) => {
      const key = `${job.title.trim().toLowerCase()}|${job.location.trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((job) => {
      const ids = jobs
        .filter(
          (j) =>
            j.title.trim().toLowerCase() === job.title.trim().toLowerCase() &&
            j.location.trim().toLowerCase() === job.location.trim().toLowerCase(),
        )
        .map((j) => j.id);
      const roleMatch =
        !!q && (job.title.toLowerCase().includes(q) || (job.ref ?? "").toLowerCase().includes(q));
      const list = apps
        .filter((a) => ids.includes(a.jobId))
        .filter((a) => (q && !roleMatch ? a.name.toLowerCase().includes(q) : true));
      return { job, list, roleMatch };
    })
    .filter(({ list, roleMatch }) => !q || roleMatch || list.length > 0);

  return (
    <section className="grid gap-4">
      <div className="mb-1 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[32px] font-extrabold tracking-[-1px] text-[#0B2160]">Shortlist</h1>
          <p className="mt-1.5 text-[15px] text-[#5E6380]">
            Here are your roles, with the people you have shortlisted underneath each one. Open a role to see its shortlist.
          </p>
        </div>
        <label className="flex h-11 w-full min-w-[220px] items-center gap-2 rounded-[4px] border border-[#D6D3C8] bg-white px-3 sm:w-[300px]">
          <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5E6380" strokeWidth="2.2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
          <span className="sr-only">Search shortlisted candidates</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, role or reference..."
            className="min-w-0 flex-1 border-0 bg-transparent text-[15px] text-[#0B2160] outline-none placeholder:text-[#7A7F99]"
          />
        </label>
      </div>

      {groups.length === 0 ? (
        <div className={`${panelClass} px-6 py-12`}>
          <Empty text={q ? "No shortlisted candidates match your search." : "You have no roles yet. Post a role to start shortlisting."} />
        </div>
      ) : null}

      {groups.length > 0 ? (
        <div className="grid gap-4">
          {groups.map(({ job, list }) => {
            const isOpen = open === job.id || (q ? true : false);
            return (
              <article key={job.id} className={`${panelClass} overflow-hidden`}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : job.id)}
                  className="flex w-full flex-wrap items-center justify-between gap-4 px-6 py-4 text-left transition hover:bg-[#F7F8FC]"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      className={`text-[13px] text-[#5E6380] transition-transform ${isOpen ? "rotate-90" : ""}`}
                      aria-hidden
                    >
                      ▶
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2.5">
                        <p className="text-[19px] font-extrabold tracking-[-0.3px] text-[#0B2160]">{job.title}</p>
                        <StatusPill status={jobStatusLabel(job)} />
                        <AccessPill state={accessFor(job.id)} />
                      </div>
                      <p className="mt-0.5 text-[13px] text-[#5E6380]">{job.location}</p>
                      {job.ref ? (
                        <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#5E6380]">
                          Ref {job.ref}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <span className="text-[13px] font-semibold text-[#5E6380]">
                    {list.length === 0
                      ? "No shortlist yet"
                      : `${list.length} shortlisted ${list.length === 1 ? "person" : "people"}`}
                  </span>
                </button>

                {isOpen ? (
                  <div className="border-t border-[#ECEAE3] bg-[#FAF9F5]">
                    {list.length === 0 ? (
                      <div className="px-6 py-8">
                        <Empty text="Nobody is shortlisted for this role yet. Shortlist candidates from View applications and they will appear here." />
                      </div>
                    ) : (
                      <div>
                        <p className="px-6 pt-4 text-[12px] font-bold uppercase tracking-[0.16em] text-[#5E6380]">
                          Shortlisted for {job.title}
                        </p>
                        <div className="mt-2 divide-y divide-[#ECEAE3]">
                          {list.map((a) => (
                            <ApplicationRow
                              key={a.id}
                              app={a}
                              jobId={a.jobId}
                              pro={accessFor(a.jobId).unlocked}
                              onView={() => {}}
                              onStatus={onStatus}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

function ApplicationRow({
  app,
  jobId,
  pro,
  onView,
  onStatus,
}: {
  app: Application;
  jobId: string;
  pro: boolean;
  onView: (appId: string) => void;
  onStatus: (id: string, status: Application["status"]) => void;
}) {
  const [open, setOpen] = useState(false);
  const shownName = pro ? app.name : firstNameOf(app.name);

  return (
    <div className="px-5 py-3.5">
      <div className="grid gap-4 lg:grid-cols-[minmax(210px,1fr)_260px_auto] lg:items-center">
        <div className="flex min-w-0 items-center gap-4">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#E8ECF8] text-[14px] font-extrabold text-[#0B2160]">
            {initialsOf(shownName)}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <p className="text-[16px] font-extrabold text-[#0B2160]">{shownName}</p>
              <StatusPill status={app.status === "Rejected" ? "Not a fit" : app.status} />
            </div>
            <p className="mt-0.5 text-[13px] text-[#5E6380]">
              {app.years} · {app.location} · {app.appliedAt}
            </p>
          </div>
        </div>

        <PitchPlayer label="60-second pitch" available={app.hasPitch} url={app.audioUrl} className="w-full" />

        <div className="flex flex-wrap justify-start gap-2 lg:justify-end">
          <button
            type="button"
            onClick={() => {
              if (!open) onView(app.id);
              setOpen((v) => !v);
            }}
            className="inline-flex h-[42px] items-center justify-center rounded-[4px] border-[1.5px] border-[#D6D3C8] bg-white px-3.5 text-[14px] font-extrabold text-[#0B2160]"
          >
            {open ? "Close application" : "Open application"}
          </button>
          <button
            type="button"
            onClick={() => onStatus(app.id, app.status === "Shortlisted" ? "New" : "Shortlisted")}
            className={`${app.status === "Shortlisted" ? "border-[1.5px] border-[#0B2160] bg-[#0B2160] text-white" : "border-[1.5px] border-[#0B2160] bg-white text-[#0B2160]"} inline-flex h-[42px] min-w-[128px] items-center justify-center rounded-[4px] px-3.5 text-[14px] font-extrabold`}
          >
            {app.status === "Shortlisted" ? "Shortlisted" : "Shortlist"}
          </button>
          <button
            type="button"
            onClick={() => onStatus(app.id, app.status === "Rejected" ? "New" : "Rejected")}
            className={`${app.status === "Rejected" ? "border-[1.5px] border-[#A62B12] bg-[#FCE3DD] text-[#A62B12]" : "border-[1.5px] border-[#D6D3C8] bg-white text-[#4A4F6A]"} inline-flex h-[42px] min-w-[110px] items-center justify-center rounded-[4px] px-3.5 text-[14px] font-extrabold`}
          >
            {app.status === "Rejected" ? "Not a fit" : "Not a fit"}
          </button>
        </div>
      </div>

      {open ? (
        <div className="mt-5 rounded-lg border border-[#ECEAE3] bg-[#FAF9F5] p-5">
          <div className="grid gap-5">
            <div className="grid gap-4">
              <div className="rounded-lg border border-[#ECEAE3] bg-white p-4">
                <p className={labelClass}>Candidate details</p>
                <p className="mt-2 text-[16px] font-semibold text-ink">{shownName}</p>
                <div className="mt-3 grid gap-2 text-[14px]">
                  <Fact label="Full name" value={pro ? app.name : "Hidden until you unlock"} />
                  <Fact
                    label="Email"
                    value={pro ? app.email || "Not provided" : maskEmail(app.email)}
                    href={pro && app.email ? `mailto:${app.email}` : undefined}
                  />
                  <Fact
                    label="Phone"
                    value={pro ? app.phone || "Not provided" : maskPhone(app.phone)}
                    href={pro && app.phone ? `tel:${app.phone.replace(/\s+/g, "")}` : undefined}
                  />
                  <Fact label="Location" value={app.location || "Not provided"} />
                </div>
                {!pro ? (
                  <UnlockCandidateCta jobId={jobId} className="mt-4" />
                ) : null}
              </div>

              <PitchPlayer label="60-second pitch" available={app.hasPitch} url={app.audioUrl} className="w-full" />

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => onStatus(app.id, app.status === "Shortlisted" ? "New" : "Shortlisted")}
                  className={app.status === "Shortlisted" ? ghostClass : btnClass}
                >
                  {app.status === "Shortlisted" ? "Remove from shortlist" : "Shortlist"}
                </button>
                <button type="button" onClick={() => onStatus(app.id, "Rejected")} className={dangerClass}>
                  Not a fit
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Fact({ label, value, href }: { label: string; value: string; href?: string | undefined }) {
  return FactRow({ label, value, href });
}

function PitchPlayer({
  label,
  available,
  url,
  emptyLabel = "No pitch recorded",
  className = "",
}: {
  label: string;
  available: boolean;
  /** Real, playable recording URL. May be absent even when `available` is true — see AddAudioUrlToApplicationsTable. */
  url?: string | undefined;
  emptyLabel?: string;
  className?: string;
}) {
  return (
    <div
      className={`flex h-[82px] min-w-0 flex-col justify-between overflow-hidden rounded-xl border border-[#ECEAE3] bg-[#f6f8fc] px-3.5 py-2.5 ${className}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-[#5E6380]">{label}</span>
        {!available ? (
          <span className="shrink-0 text-[11px] font-semibold text-ink/35">{emptyLabel}</span>
        ) : !url ? (
          <span className="shrink-0 text-[11px] font-semibold text-ink/35">Recording unavailable</span>
        ) : null}
      </div>
      {available && url ? (
        <audio controls src={url} className="block h-8 min-h-8 w-full max-w-full" />
      ) : (
        <div className="h-8 w-full rounded-md border border-dashed border-[#BDBDB5]" />
      )}
    </div>
  );
}

function FactRow({ label, value, href }: { label: string; value: string; href?: string | undefined }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-purple-strong/[0.07] pb-2 last:border-0 last:pb-0">
      <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-[#5E6380]">{label}</span>
      {href ? (
        <a href={href} className="truncate text-[14px] font-semibold text-purple-strong hover:underline">
          {value}
        </a>
      ) : (
        <span className="truncate text-[14px] text-[#2B3150]">{value}</span>
      )}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <p className="rounded-md border border-dashed border-[#BDBDB5] px-6 py-8 text-center text-[15px] text-[#5E6380]">
      {text}
    </p>
  );
}

/**
 * A job always starts status="Live" the moment it's posted, but that only
 * means the employer switched it on — CLOSDD still has to approve it before
 * it's actually visible on the public board (see GET /api/public/jobs,
 * which requires decision="Approved"). Showing "Live" here regardless made
 * it look published immediately, before any admin action. `decision` is
 * only optional on the type for old client-side demo/seed jobs that never
 * had a review step; a real job from the API always carries one.
 */
function jobStatusLabel(job: EmployerJob): string {
  if (job.status === "Live" && job.decision !== undefined && job.decision !== "Approved") {
    return job.decision === "Rejected" ? "Not approved" : "Pending approval";
  }
  return job.status;
}

function StatusPill({ status }: { status: string }) {
  const k = status.toLowerCase();
  const tone = k.includes("interview")
    ? "bg-[#FFE1D6] text-[#8A2A12]"
    : k.includes("live") || k.includes("hired") || k.includes("approved")
      ? "bg-[#E2F3E9] text-[#0B6B40]"
      : k.includes("shortlist")
        ? "bg-[#0B2160] text-white"
        : k.includes("reject") || k.includes("not approved")
          ? "bg-[#FCE3DD] text-[#A62B12]"
          : k.includes("new") || k.includes("pending") || k.includes("review")
            ? "bg-[#FFE9A8] text-[#6B4A00]"
            : "bg-[#EEEDE7] text-[#4A4F6A]";
  return <span className={`inline-flex items-center rounded-full px-2.5 py-[3px] text-[12px] font-bold ${tone}`}>{status}</span>;
}

const seniorityOptions = ["Entry level / no experience", "1–2 years", "3–5 years", "5+ years", "Leadership"];
const contractOptions = ["Full-time", "Part-time", "Contract / freelance", "Internship"];
const settingOptions = ["On-site", "Hybrid", "Remote"];
const payStructureOptions = [
  "Base salary + commission",
  "Base salary only",
  "Commission only",
  "Hourly + bonus",
  "Day rate",
];

/** Same field set and layout as the admin's role editor, so a job looks and edits the same way on both sides. */
function EditJob({ job, onSave, submitLabel = "Save changes" }: { job: EmployerJob; onSave: (j: EmployerJob) => void; submitLabel?: string }) {
  const [draft, setDraft] = useState<EmployerJob>(job);
  const set = (patch: Partial<EmployerJob>) => setDraft((d) => ({ ...d, ...patch }));
  const tags = draft.tags ?? [];
  const toggleTag = (id: string) =>
    set({ tags: tags.includes(id) ? tags.filter((t) => t !== id) : [...tags, id] });

  return (
    <form
      className="mt-6 grid gap-5 rounded-xl border border-[#ECEAE3] bg-[#f6f8fc] p-5"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(draft);
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="grid gap-1.5">
          <span className={labelClass}>Job title</span>
          <input required maxLength={100} value={draft.title} onChange={(e) => set({ title: e.target.value })} className={inputClass} />
        </label>
        <label className="grid gap-1.5">
          <span className={labelClass}>Reference</span>
          <input maxLength={60} value={draft.ref} onChange={(e) => set({ ref: e.target.value })} className={inputClass} />
        </label>
        <label className="grid gap-1.5">
          <span className={labelClass}>Seniority</span>
          <select value={draft.seniority} onChange={(e) => set({ seniority: e.target.value })} className={inputClass}>
            {[draft.seniority, ...seniorityOptions.filter((o) => o !== draft.seniority)].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className={labelClass}>Contract</span>
          <select value={draft.contract} onChange={(e) => set({ contract: e.target.value })} className={inputClass}>
            {[draft.contract, ...contractOptions.filter((o) => o !== draft.contract)].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="grid gap-1.5">
          <span className={labelClass}>Company name</span>
          <input
            maxLength={100}
            value={draft.company}
            onChange={(e) => set({ company: e.target.value })}
            className={inputClass}
          />
        </label>
        <label className="grid gap-1.5">
          <span className={labelClass}>Location</span>
          <input maxLength={80} value={draft.location} onChange={(e) => set({ location: e.target.value })} className={inputClass} />
        </label>
        <label className="grid gap-1.5">
          <span className={labelClass}>Setting</span>
          <select value={draft.setting} onChange={(e) => set({ setting: e.target.value })} className={inputClass}>
            {[draft.setting, ...settingOptions.filter((o) => o !== draft.setting)].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="grid gap-1.5">
          <span className={labelClass}>Pay structure</span>
          <select value={draft.payStructure} onChange={(e) => set({ payStructure: e.target.value })} className={inputClass}>
            {[draft.payStructure, ...payStructureOptions.filter((o) => o !== draft.payStructure)]
              .filter(Boolean)
              .map((o) => (
                <option key={o}>{o}</option>
              ))}
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className={labelClass}>Pay details</span>
          <input
            maxLength={160}
            value={draft.pay}
            onChange={(e) => set({ pay: e.target.value })}
            placeholder="£35,000 – £45,000 base + commission"
            className={inputClass}
          />
        </label>
      </div>
      <label className="grid gap-1.5">
        <span className={labelClass}>The advert</span>
        <textarea rows={6} value={draft.advert} onChange={(e) => set({ advert: e.target.value })} className={areaClass} />
      </label>
      <div className="grid gap-2">
        <span className={labelClass}>Tags</span>
        <div className="grid gap-3">
          {tagGroups.map((group) => (
            <div key={group}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink/35">{group}</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {jobTags
                  .filter((t) => t.group === group)
                  .map((t) => {
                    const on = tags.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleTag(t.id)}
                        className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] transition ${
                          on
                            ? `${toneClass[t.tone]} ring-2 ring-purple-strong/25`
                            : "bg-white text-[#5E6380] ring-1 ring-ink/10 hover:ring-purple-strong/25"
                        }`}
                      >
                        {t.label}
                      </button>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <button type="submit" className={`${btnClass} w-fit`}>
        {submitLabel}
      </button>
    </form>
  );
}

function AccountPanel({
  account,
  onSave,
  jobs,
  accessFor,
  countFor,
}: {
  account: EmployerAccount;
  onSave: (a: EmployerAccount) => void;
  jobs: EmployerJob[];
  accessFor: (id: string) => RoleAccessState;
  countFor: (id: string) => number;
}) {
  const [draft, setDraft] = useState<EmployerAccount>(account);
  const [saved, setSaved] = useState(false);
  const set = (patch: Partial<EmployerAccount>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setSaved(false);
  };

  const lab = "grid gap-1.5 text-[14px] font-bold text-[#2B3150]";
  const field =
    "h-[46px] w-full rounded-[4px] border border-[#D6D3C8] bg-white px-3 text-[15px] font-medium text-[#0B2160] outline-none transition focus:border-[#0B2160] focus:ring-2 focus:ring-[#FF5A3C]/40";
  const unlocked = jobs.filter((j) => accessFor(j.id).unlocked).length;
  const totalApplicants = jobs.reduce((n, j) => n + countFor(j.id), 0);

  return (
    <div className="mx-auto grid w-full max-w-[760px] gap-4">
      <div className="mb-1">
        <h1 className="text-[32px] font-extrabold tracking-[-1px] text-[#0B2160]">Account</h1>
        <p className="mt-1.5 text-[15px] text-[#5E6380]">
          Your details are for CLOSDD only. They are never shared with candidates.
        </p>
      </div>

      <section id="plan" aria-labelledby="plan-h" className={`${panelClass} p-[22px]`}>
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
          <h2 id="plan-h" className="text-[19px] font-extrabold text-[#0B2160]">Your plan</h2>
          <span className="text-[14px] text-[#5E6380]">
            {jobs.length ? `${unlocked} of ${jobs.length} role${jobs.length === 1 ? "" : "s"} unlocked · ${totalApplicants} applicant${totalApplicants === 1 ? "" : "s"}` : "No roles yet"}
          </span>
        </div>
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
          <div className="rounded-lg border-2 border-[#0B2160] bg-white p-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[16px] font-extrabold text-[#0B2160]">Pay per role</p>
              <span className="rounded-full bg-[#0B2160] px-2.5 py-[3px] text-[12px] font-bold text-white">Current plan</span>
            </div>
            <p className="mt-1.5 text-[24px] font-extrabold text-[#0B2160]">£99 <span className="text-[14px] font-semibold text-[#5E6380]">one-off per role</span></p>
            <p className="mt-1.5 text-[14px] leading-[1.5] text-[#4A4F6A]">
              Unlock a role to see every applicant's details. Money back within 7 days if nobody is worth a call.
            </p>
          </div>
          <div className="rounded-lg border border-[#E3E1D9] bg-white p-4">
            <p className="text-[16px] font-extrabold text-[#0B2160]">Monthly</p>
            <p className="mt-1.5 text-[24px] font-extrabold text-[#0B2160]">£289 <span className="text-[14px] font-semibold text-[#5E6380]">a month</span></p>
            <p className="mt-1.5 text-[14px] leading-[1.5] text-[#4A4F6A]">
              Up to 2 live roles at once. Unlimited applications, and every applicant is unlocked. Cancel any time.
            </p>
            <Link to="/contact" className="mt-3 inline-flex h-10 items-center rounded-[4px] bg-[#FF5A3C] px-3.5 text-[14px] font-extrabold text-[#0B2160]">
              Switch to monthly
            </Link>
          </div>
        </div>
      </section>

      <form
        aria-labelledby="det-h"
        className={`${panelClass} p-[22px]`}
        onSubmit={(e) => {
          e.preventDefault();
          onSave(draft);
          setSaved(true);
        }}
      >
        <h2 id="det-h" className="mb-[18px] text-[19px] font-extrabold text-[#0B2160]">Your details</h2>
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
          <label className={lab}>
            Company name
            <input maxLength={100} value={draft.company} onChange={(e) => set({ company: e.target.value })} className={field} />
          </label>
          <label className={lab}>
            Contact name
            <input maxLength={100} value={draft.contactName} onChange={(e) => set({ contactName: e.target.value })} className={field} />
          </label>
          <label className={lab}>
            Work email
            <input type="email" maxLength={255} value={draft.email} onChange={(e) => set({ email: e.target.value })} className={field} />
          </label>
          <label className={lab}>
            Phone number
            <input type="tel" maxLength={30} value={draft.phone} onChange={(e) => set({ phone: e.target.value })} className={field} />
          </label>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3.5">
          <button type="submit" className="h-11 rounded-[4px] bg-[#0B2160] px-5 text-[15px] font-extrabold text-white">
            Save details
          </button>
          {saved ? <span role="status" className="text-[14px] font-bold text-[#0B6B40]">Details saved</span> : null}
        </div>
      </form>

      <EmployerPasswordSection />
    </div>
  );
}

function EmployerPasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function submit() {
    setError(null);
    setSaved(false);
    if (next.length < 8) {
      setError("Your new password needs at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("The new passwords don't match.");
      return;
    }
    try {
      await updateEmployerPassword(current, next);
      setSaved(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't update your password.");
    }
  }

  return (
    <section aria-labelledby="pw-h" className={`${panelClass} p-[22px]`}>
      <h2 id="pw-h" className="text-[19px] font-extrabold text-[#0B2160]">Password</h2>
      <p className="mb-[18px] mt-1 text-[14px] text-[#5E6380]">Update the password you use to log in.</p>
      <div className="grid gap-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="grid gap-1.5">
            <span className="text-[14px] font-bold text-[#2B3150]">Current password</span>
            <PasswordInput
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={`${inputClass} pr-10`}
              iconClassName="absolute inset-y-0 right-3 flex items-center text-[#5E6380] hover:text-[#4A4F6A]"
              autoComplete="current-password"
            />
          </label>
          <label className="grid gap-1.5">
            <span className="text-[14px] font-bold text-[#2B3150]">New password</span>
            <PasswordInput
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={`${inputClass} pr-10`}
              iconClassName="absolute inset-y-0 right-3 flex items-center text-[#5E6380] hover:text-[#4A4F6A]"
              autoComplete="new-password"
            />
          </label>
          <label className="grid gap-1.5">
            <span className="text-[14px] font-bold text-[#2B3150]">Confirm new password</span>
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={`${inputClass} pr-10`}
              iconClassName="absolute inset-y-0 right-3 flex items-center text-[#5E6380] hover:text-[#4A4F6A]"
              autoComplete="new-password"
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <button type="button" onClick={() => void submit()} className={btnClass}>
            Update password
          </button>
          {error ? <span className="text-[14px] font-semibold text-[#4A4F6A]">{error}</span> : null}
          {saved ? <span className="text-[14px] font-semibold text-[#0B6B40]">Password updated.</span> : null}
        </div>
      </div>
    </section>
  );
}

function TypeToConfirmModal({
  open,
  title,
  message,
  confirmWord,
  value,
  onChange,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmWord: string;
  value: string;
  onChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  const matched = value.trim().toLowerCase() === confirmWord.toLowerCase();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-purple-strong/60" onClick={onCancel} aria-hidden />
      <div className="relative w-full max-w-[420px] rounded-xl border border-[#ECEAE3] bg-white p-6 shadow-xl">
        <h3 className="font-serif text-[17px] tracking-[-0.01em] text-ink">{title}</h3>
        <p className="mt-2 text-[15px] leading-[1.55] text-[#4A4F6A]">{message}</p>
        <label className="mt-5 grid gap-2">
          <span className={labelClass}>Type {confirmWord} to confirm</span>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={confirmWord}
            className={inputClass}
          />
        </label>
        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          <button type="button" onClick={onCancel} className={ghostClass}>
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={!matched} className={`${dangerClass} disabled:opacity-40`}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
